import { AeadId, KdfId, KemId, KeyConfig } from "ohttp-ts";

// ============================================================================
// Types
// ============================================================================

type ParsedKeyConfig = {
	keyId: number;
	kemId: number;
	publicKey: Uint8Array;
	symmetricAlgorithms: ReadonlyArray<{ kdfId: number; aeadId: number }>;
};

type Result<T, E = Error> = { ok: true; value: T } | { ok: false; error: E };

// ============================================================================
// Algorithm Name Mappings (RFC 9180)
// ============================================================================

const KEM_NAMES: Record<number, string> = {
	[KemId.P256_HKDF_SHA256]: "DHKEM(P-256, HKDF-SHA256)",
	[KemId.P384_HKDF_SHA384]: "DHKEM(P-384, HKDF-SHA384)",
	[KemId.P521_HKDF_SHA512]: "DHKEM(P-521, HKDF-SHA512)",
	[KemId.X25519_HKDF_SHA256]: "DHKEM(X25519, HKDF-SHA256)",
	[KemId.X448_HKDF_SHA512]: "DHKEM(X448, HKDF-SHA512)",
	// Post-quantum
	[KemId.ML_KEM_512]: "ML-KEM-512",
	[KemId.ML_KEM_768]: "ML-KEM-768",
	[KemId.ML_KEM_1024]: "ML-KEM-1024",
	[KemId.MLKEM768_P256]: "ML-KEM-768 + P-256",
	[KemId.MLKEM1024_P384]: "ML-KEM-1024 + P-384",
	[KemId.MLKEM768_X25519]: "ML-KEM-768 + X25519",
};

const KDF_NAMES: Record<number, string> = {
	[KdfId.HKDF_SHA256]: "HKDF-SHA256",
	[KdfId.HKDF_SHA384]: "HKDF-SHA384",
	[KdfId.HKDF_SHA512]: "HKDF-SHA512",
};

const AEAD_NAMES: Record<number, string> = {
	[AeadId.AES_128_GCM]: "AES-128-GCM",
	[AeadId.AES_256_GCM]: "AES-256-GCM",
	[AeadId.ChaCha20Poly1305]: "ChaCha20-Poly1305",
};

function getKemName(id: number): string {
	return KEM_NAMES[id] ?? `Unknown (0x${id.toString(16).padStart(4, "0")})`;
}

function getKdfName(id: number): string {
	return KDF_NAMES[id] ?? `Unknown (0x${id.toString(16).padStart(4, "0")})`;
}

function getAeadName(id: number): string {
	return AEAD_NAMES[id] ?? `Unknown (0x${id.toString(16).padStart(4, "0")})`;
}

// ============================================================================
// Utilities
// ============================================================================

function toHex(bytes: Uint8Array): string {
	return Array.from(bytes)
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
}

function toBase64(bytes: Uint8Array): string {
	return btoa(String.fromCharCode(...bytes));
}

function fromBase64(str: string): Result<Uint8Array> {
	try {
		const binary = atob(str);
		const bytes = new Uint8Array(binary.length);
		for (let i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i);
		}
		return { ok: true, value: bytes };
	} catch {
		return { ok: false, error: new Error("Invalid base64 encoding") };
	}
}

function fromHex(str: string): Result<Uint8Array> {
	const clean = str.replace(/\s/g, "").toLowerCase();
	if (!/^[0-9a-f]*$/.test(clean)) {
		return { ok: false, error: new Error("Invalid hex encoding") };
	}
	if (clean.length % 2 !== 0) {
		return { ok: false, error: new Error("Hex string must have even length") };
	}
	const bytes = new Uint8Array(clean.length / 2);
	for (let i = 0; i < bytes.length; i++) {
		const byte = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
		if (Number.isNaN(byte)) {
			return { ok: false, error: new Error(`Invalid hex at position ${i * 2}`) };
		}
		bytes[i] = byte;
	}
	return { ok: true, value: bytes };
}

function detectAndDecodeBytes(input: string): Result<Uint8Array> {
	const trimmed = input.trim();

	// Try hex first (if it looks like hex)
	if (/^[0-9a-fA-F\s]+$/.test(trimmed)) {
		const hexResult = fromHex(trimmed);
		if (hexResult.ok) {
			return hexResult;
		}
	}

	// Try base64
	return fromBase64(trimmed);
}

// ============================================================================
// Key Config Parsing
// ============================================================================

function parseKeyConfigs(bytes: Uint8Array): Result<ParsedKeyConfig[]> {
	try {
		const configs = KeyConfig.parseMultiple(bytes);
		return {
			ok: true,
			value: configs.map((config) => ({
				keyId: config.keyId,
				kemId: config.kemId,
				publicKey: config.publicKey,
				symmetricAlgorithms: config.symmetricAlgorithms,
			})),
		};
	} catch (e) {
		const message = e instanceof Error ? e.message : "Unknown parse error";
		return { ok: false, error: new Error(message) };
	}
}

function parseSingleKeyConfig(bytes: Uint8Array): Result<ParsedKeyConfig> {
	try {
		const config = KeyConfig.parse(bytes);
		return {
			ok: true,
			value: {
				keyId: config.keyId,
				kemId: config.kemId,
				publicKey: config.publicKey,
				symmetricAlgorithms: config.symmetricAlgorithms,
			},
		};
	} catch (e) {
		const message = e instanceof Error ? e.message : "Unknown parse error";
		return { ok: false, error: new Error(message) };
	}
}

// ============================================================================
// Display Formatting
// ============================================================================

function formatKeyConfig(config: ParsedKeyConfig, index?: number): string {
	const lines: string[] = [];

	if (index !== undefined) {
		lines.push(`Key Config #${index + 1}`);
		lines.push("─".repeat(40));
	}

	lines.push(`Key ID:        ${config.keyId} (0x${config.keyId.toString(16).padStart(2, "0")})`);
	lines.push(`KEM ID:        ${config.kemId} → ${getKemName(config.kemId)}`);
	lines.push(`Public Key:    ${toBase64(config.publicKey)}`);
	lines.push(`               (${config.publicKey.length} bytes)`);
	lines.push("");
	lines.push("Symmetric Algorithms:");

	for (const algo of config.symmetricAlgorithms) {
		lines.push(`  • KDF: ${algo.kdfId} → ${getKdfName(algo.kdfId)}`);
		lines.push(`    AEAD: ${algo.aeadId} → ${getAeadName(algo.aeadId)}`);
	}

	return lines.join("\n");
}

function formatKeyConfigsDisplay(configs: ParsedKeyConfig[]): string {
	if (configs.length === 0) {
		return "No key configurations found";
	}

	if (configs.length === 1) {
		const config = configs[0];
		if (config === undefined) {
			return "No key configurations found";
		}
		return formatKeyConfig(config);
	}

	const separator = `\n\n${"═".repeat(50)}\n\n`;
	return configs.map((config, i) => formatKeyConfig(config, i)).join(separator);
}

// ============================================================================
// Validation
// ============================================================================

type ValidationResult = {
	valid: boolean;
	kemSupported: boolean;
	algorithmsSupported: boolean;
	warnings: string[];
};

function validateKeyConfig(config: ParsedKeyConfig): ValidationResult {
	const warnings: string[] = [];

	// Check KEM support (WebCrypto-compatible KEMs)
	const supportedKems: readonly number[] = [
		KemId.P256_HKDF_SHA256,
		KemId.P384_HKDF_SHA384,
		KemId.P521_HKDF_SHA512,
		KemId.X25519_HKDF_SHA256,
	];
	const kemSupported = supportedKems.includes(config.kemId);
	if (!kemSupported) {
		warnings.push(`KEM ${getKemName(config.kemId)} may not be supported in all browsers`);
	}

	// Check algorithm support
	let algorithmsSupported = true;
	for (const algo of config.symmetricAlgorithms) {
		if (!(algo.kdfId in KDF_NAMES)) {
			warnings.push(`Unknown KDF ID: 0x${algo.kdfId.toString(16)}`);
			algorithmsSupported = false;
		}
		if (!(algo.aeadId in AEAD_NAMES)) {
			warnings.push(`Unknown AEAD ID: 0x${algo.aeadId.toString(16)}`);
			algorithmsSupported = false;
		}
	}

	return {
		valid: kemSupported && algorithmsSupported,
		kemSupported,
		algorithmsSupported,
		warnings,
	};
}

function formatValidationResult(config: ParsedKeyConfig, validation: ValidationResult): string {
	const lines: string[] = [];

	if (validation.valid) {
		lines.push("✓ Key configuration is valid");
	} else {
		lines.push("⚠ Key configuration has issues");
	}

	lines.push("");
	lines.push(formatKeyConfig(config));
	lines.push("");
	lines.push("─".repeat(40));
	lines.push("Validation Results:");
	lines.push(`  KEM supported:        ${validation.kemSupported ? "✓" : "✗"}`);
	lines.push(`  Algorithms supported: ${validation.algorithmsSupported ? "✓" : "✗"}`);

	if (validation.warnings.length > 0) {
		lines.push("");
		lines.push("Warnings:");
		for (const warning of validation.warnings) {
			lines.push(`  ⚠ ${warning}`);
		}
	}

	return lines.join("\n");
}

// ============================================================================
// Byte-level Inspection
// ============================================================================

function inspectKeyConfigBytes(bytes: Uint8Array): string {
	const lines: string[] = [];
	lines.push(`Total bytes: ${bytes.length}`);
	lines.push(`Hex: ${toHex(bytes)}`);
	lines.push("");

	// Try to parse and show structure
	const parseResult = parseSingleKeyConfig(bytes);

	if (!parseResult.ok) {
		// Try parsing as multiple configs (with length prefix)
		const multiResult = parseKeyConfigs(bytes);
		if (multiResult.ok) {
			lines.push("Parsed as application/ohttp-keys (multiple configs):");
			lines.push("");
			lines.push(formatKeyConfigsDisplay(multiResult.value));
		} else {
			lines.push(`Parse error: ${parseResult.error.message}`);
			lines.push("");
			lines.push("Raw byte breakdown:");
			lines.push(formatRawBytes(bytes));
		}
	} else {
		lines.push("Parsed as single key config:");
		lines.push("");
		lines.push(formatKeyConfig(parseResult.value));
		lines.push("");
		lines.push("Byte structure:");
		lines.push(formatKeyConfigStructure(bytes, parseResult.value));
	}

	return lines.join("\n");
}

function formatRawBytes(bytes: Uint8Array): string {
	const lines: string[] = [];
	const bytesPerLine = 16;

	for (let i = 0; i < bytes.length; i += bytesPerLine) {
		const slice = bytes.slice(i, Math.min(i + bytesPerLine, bytes.length));
		const hex = Array.from(slice)
			.map((b) => b.toString(16).padStart(2, "0"))
			.join(" ");
		const offset = i.toString(16).padStart(4, "0");
		lines.push(`${offset}: ${hex}`);
	}

	return lines.join("\n");
}

function formatKeyConfigStructure(bytes: Uint8Array, config: ParsedKeyConfig): string {
	const lines: string[] = [];
	let offset = 0;

	// Key ID (1 byte)
	lines.push(`[${offset.toString().padStart(3)}] Key ID: 0x${toHex(bytes.slice(0, 1))}`);
	offset += 1;

	// KEM ID (2 bytes)
	lines.push(`[${offset.toString().padStart(3)}] KEM ID: 0x${toHex(bytes.slice(1, 3))}`);
	offset += 2;

	// Public key (Npk bytes)
	const pkLen = config.publicKey.length;
	lines.push(
		`[${offset.toString().padStart(3)}] Public Key: ${pkLen} bytes (0x${toHex(bytes.slice(3, 3 + pkLen).slice(0, 8))}...)`,
	);
	offset += pkLen;

	// Symmetric algorithms length (2 bytes)
	const algoLenOffset = 3 + pkLen;
	lines.push(
		`[${algoLenOffset.toString().padStart(3)}] Algo Length: 0x${toHex(bytes.slice(algoLenOffset, algoLenOffset + 2))}`,
	);
	offset = algoLenOffset + 2;

	// Each symmetric algorithm (4 bytes each)
	for (let i = 0; i < config.symmetricAlgorithms.length; i++) {
		const algo = config.symmetricAlgorithms[i];
		if (algo === undefined) continue;
		lines.push(
			`[${offset.toString().padStart(3)}] Algorithm ${i + 1}: KDF=0x${algo.kdfId.toString(16).padStart(4, "0")} AEAD=0x${algo.aeadId.toString(16).padStart(4, "0")}`,
		);
		offset += 4;
	}

	return lines.join("\n");
}

// ============================================================================
// Autofill Functions
// ============================================================================

function setInputValue(sectionId: string, inputName: string, value: string): void {
	const section = document.querySelector<HTMLElement>(`section#${sectionId}`);
	if (!section) return;

	const input = section.querySelector<HTMLInputElement>(`input[name="${inputName}"]`);
	if (input) {
		input.value = value;
	}
}

function autofillFromFetch(rawBytes: Uint8Array): void {
	const base64 = toBase64(rawBytes);

	// Autofill validate section
	setInputValue("validate", "key-config", base64);

	// Autofill inspect section
	setInputValue("inspect", "key-bytes", base64);
}

// ============================================================================
// URL Query Parameter Support
// ============================================================================

type QueryParams = {
	gateway_url?: string | undefined;
	key_config?: string | undefined;
	key_bytes?: string | undefined;
};

function parseQueryParams(): QueryParams {
	const params = new URLSearchParams(window.location.search);
	return {
		gateway_url: params.get("gateway_url") ?? undefined,
		key_config: params.get("key_config") ?? undefined,
		key_bytes: params.get("key_bytes") ?? undefined,
	};
}

function applyQueryParams(params: QueryParams): void {
	if (params.gateway_url) {
		setInputValue("fetch", "gateway-url", params.gateway_url);
	}
	if (params.key_config) {
		setInputValue("validate", "key-config", params.key_config);
	}
	if (params.key_bytes) {
		setInputValue("inspect", "key-bytes", params.key_bytes);
	}
}

// ============================================================================
// Form Handlers
// ============================================================================

function setDisplay(
	sectionId: string,
	content: string,
	type: "normal" | "error" | "success",
): void {
	const section = document.querySelector<HTMLElement>(`section#${sectionId}`);
	if (!section) return;

	const display = section.querySelector<HTMLElement>(".display");
	if (!display) return;

	display.textContent = content;
	display.classList.remove("error", "success");
	if (type === "error") {
		display.classList.add("error");
	} else if (type === "success") {
		display.classList.add("success");
	}
}

function getFormValue(form: HTMLFormElement, name: string): string | undefined {
	const element = form.elements.namedItem(name);
	if (element instanceof HTMLInputElement) {
		return element.value.trim();
	}
	return undefined;
}

function getTurnstileToken(form: HTMLFormElement): string | undefined {
	const element = form.elements.namedItem("cf-turnstile-response");
	if (element instanceof HTMLInputElement) {
		return element.value;
	}
	return undefined;
}

async function handleFetchGateway(event: SubmitEvent): Promise<void> {
	event.preventDefault();

	const form = event.currentTarget;
	if (!(form instanceof HTMLFormElement)) return;

	const gatewayUrl = getFormValue(form, "gateway-url");
	const turnstileToken = getTurnstileToken(form);

	if (!gatewayUrl) {
		setDisplay("fetch", "Please enter a gateway URL", "error");
		return;
	}

	if (!turnstileToken) {
		setDisplay("fetch", "Please complete the Turnstile challenge", "error");
		return;
	}

	setDisplay("fetch", "Fetching...", "normal");

	try {
		// Construct the well-known URL
		const url = new URL(gatewayUrl);
		const targetUrl = `${url.origin}/.well-known/ohttp-gateway`;

		// Fetch via proxy
		const response = await fetch(`/proxy?target=${encodeURIComponent(targetUrl)}`, {
			headers: {
				"cf-turnstile-response": turnstileToken,
			},
		});

		// Reset Turnstile for next request
		if (typeof turnstile !== "undefined") {
			turnstile.reset("section#fetch .cf-turnstile");
		}

		if (!response.ok) {
			const text = await response.text();
			setDisplay(
				"fetch",
				`Fetch failed: ${response.status} ${response.statusText}\n${text}`,
				"error",
			);
			return;
		}

		const contentType = response.headers.get("content-type");
		if (contentType && !contentType.includes("application/ohttp-keys")) {
			setDisplay(
				"fetch",
				`Unexpected content type: ${contentType}\nExpected: application/ohttp-keys`,
				"error",
			);
			return;
		}

		const bytes = new Uint8Array(await response.arrayBuffer());
		const parseResult = parseKeyConfigs(bytes);

		if (!parseResult.ok) {
			setDisplay("fetch", `Parse error: ${parseResult.error.message}`, "error");
			return;
		}

		setDisplay("fetch", formatKeyConfigsDisplay(parseResult.value), "success");

		// Autofill other sections with the fetched data
		autofillFromFetch(bytes);
	} catch (e) {
		const message = e instanceof Error ? e.message : "Unknown error";
		setDisplay("fetch", `Error: ${message}`, "error");
	}
}

function handleValidate(event: SubmitEvent): void {
	event.preventDefault();

	const form = event.currentTarget;
	if (!(form instanceof HTMLFormElement)) return;

	const keyConfigB64 = getFormValue(form, "key-config");

	if (!keyConfigB64) {
		setDisplay("validate", "Please enter a key config", "error");
		return;
	}

	const bytesResult = fromBase64(keyConfigB64);
	if (!bytesResult.ok) {
		setDisplay("validate", `Invalid base64: ${bytesResult.error.message}`, "error");
		return;
	}

	const parseResult = parseSingleKeyConfig(bytesResult.value);
	if (!parseResult.ok) {
		// Try parsing as multiple
		const multiResult = parseKeyConfigs(bytesResult.value);
		if (multiResult.ok && multiResult.value.length > 0) {
			const firstConfig = multiResult.value[0];
			if (firstConfig !== undefined) {
				const validation = validateKeyConfig(firstConfig);
				setDisplay(
					"validate",
					formatValidationResult(firstConfig, validation),
					validation.valid ? "success" : "normal",
				);
				return;
			}
		}
		setDisplay("validate", `Parse error: ${parseResult.error.message}`, "error");
		return;
	}

	const validation = validateKeyConfig(parseResult.value);
	setDisplay(
		"validate",
		formatValidationResult(parseResult.value, validation),
		validation.valid ? "success" : "normal",
	);
}

function handleInspect(event: SubmitEvent): void {
	event.preventDefault();

	const form = event.currentTarget;
	if (!(form instanceof HTMLFormElement)) return;

	const keyBytes = getFormValue(form, "key-bytes");

	if (!keyBytes) {
		setDisplay("inspect", "Please enter key config bytes", "error");
		return;
	}

	const bytesResult = detectAndDecodeBytes(keyBytes);
	if (!bytesResult.ok) {
		setDisplay("inspect", `Decode error: ${bytesResult.error.message}`, "error");
		return;
	}

	const inspection = inspectKeyConfigBytes(bytesResult.value);
	setDisplay("inspect", inspection, "normal");
}

// ============================================================================
// Initialization
// ============================================================================

declare const turnstile:
	| {
			reset: (selector: string) => void;
	  }
	| undefined;

function init(): void {
	// Apply URL query parameters first
	const params = parseQueryParams();
	applyQueryParams(params);

	// Fetch section
	const fetchForm = document.querySelector<HTMLFormElement>("section#fetch form");
	if (fetchForm) {
		fetchForm.addEventListener("submit", (e) => {
			handleFetchGateway(e).catch(console.error);
		});
	}

	// Validate section
	const validateForm = document.querySelector<HTMLFormElement>("section#validate form");
	if (validateForm) {
		validateForm.addEventListener("submit", handleValidate);
	}

	// Inspect section
	const inspectForm = document.querySelector<HTMLFormElement>("section#inspect form");
	if (inspectForm) {
		inspectForm.addEventListener("submit", handleInspect);
	}
}

// Run on DOM ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
